import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { AdminRegime } from './AdminRegime';
import { AdminTradeSignalsConfig } from './AdminTradeSignalsConfig';
import { AdminPageViewStats } from './AdminPageViewStats';
import { AdminCalendarNotches } from './AdminCalendarNotches';
import { AdminBriefingShadow } from './AdminBriefingShadow';
import './Admin.css';

interface LogEntry {
  email: string;
  accessedAt: string;
  fileKey: string;
  userAgent?: string;
}

interface UserInfo {
  email: string;
  userId?: string;
  subscriptionTier?: string;
  effectiveTier?: string;
  subscriptionStatus?: string;
  emailVerified?: boolean;
  hourlyEmailsEnabled?: boolean;
  dailyReportEnabled?: boolean;
  disclaimerAccepted?: boolean;
  complimentaryTier?: string;
  complimentaryExpiresAt?: string;
  createdAt?: string;
  lastLogin?: string;
  algoTradingEnabled?: boolean;
  algoEngineLimit?: number;
  optionsAutoTradingEnabled?: boolean;
  tradingSnapshotEnabled?: boolean;
  spxSignalsEnabled?: boolean;
  spySignalsEnabled?: boolean;
  esSignalsEnabled?: boolean;
  mesSignalsEnabled?: boolean;
  indexSignalsEnabled?: boolean;
  notchesEnabled?: boolean;
}

interface TicketReply {
  from: string;
  message: string;
  timestamp: string;
}

interface SupportTicket {
  email: string;
  caseNumber: string;
  subject: string;
  description: string;
  category: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  replies?: TicketReply[];
}

interface SpxDailyCost {
  date: string;
  claude_usd: number;
  chatgpt_usd: number;
  total_usd: number;
  snapshots: number;
}

interface SpxCostSummary {
  total_usd: number;
  claude_usd: number;
  chatgpt_usd: number;
  avg_per_day_usd: number;
  projected_monthly_usd: number;
  total_snapshots: number;
  trading_days: number;
  tokens: {
    claude_input: number;
    claude_output: number;
    chatgpt_input: number;
    chatgpt_output: number;
  };
}

type Tab = 'file-logs' | 'users' | 'support-tickets' | 'spx-costs' | 'spx-config' | 'index-costs' | 'index-config' | 'billing-audit' | 'regime' | 'trade-signals-config' | 'page-view-stats' | 'calendar-notches' | 'briefing-shadow';

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('users');

  // File logs state
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logUsers, setLogUsers] = useState<Record<string, UserInfo>>({});
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [fileFilter, setFileFilter] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [logsLoaded, setLogsLoaded] = useState(false);

  // Users state
  const [allUsers, setAllUsers] = useState<UserInfo[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [editingUser, setEditingUser] = useState<UserInfo | null>(null);
  const [editFields, setEditFields] = useState<Record<string, any>>({});
  const [editSaving, setEditSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Trial modal state
  const [trialUser, setTrialUser] = useState<UserInfo | null>(null);
  const [trialTier, setTrialTier] = useState<string>('premier');
  const [trialDuration, setTrialDuration] = useState<string>('7');
  const [trialCustomDate, setTrialCustomDate] = useState<string>('');
  const [trialSaving, setTrialSaving] = useState(false);

  // Support tickets state
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState<string | null>(null);
  const [ticketsLoaded, setTicketsLoaded] = useState(false);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('');
  const [ticketEmailSearch, setTicketEmailSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [ticketStatusUpdating, setTicketStatusUpdating] = useState(false);

  // SPX config state
  const [spxMinCredit, setSpxMinCredit] = useState<number>(30);
  const [spxMinCreditInput, setSpxMinCreditInput] = useState<string>('30');
  const [spxConfigLoading, setSpxConfigLoading] = useState(false);
  const [spxConfigLoaded, setSpxConfigLoaded] = useState(false);
  const [spxConfigSaving, setSpxConfigSaving] = useState(false);
  const [spxConfigError, setSpxConfigError] = useState<string | null>(null);
  const [spxConfigUpdatedAt, setSpxConfigUpdatedAt] = useState<string | null>(null);

  // SPX costs state
  const [spxCostSummary, setSpxCostSummary] = useState<SpxCostSummary | null>(null);
  const [spxDailyCosts, setSpxDailyCosts] = useState<SpxDailyCost[]>([]);
  const [spxCostsLoading, setSpxCostsLoading] = useState(false);
  const [spxCostsError, setSpxCostsError] = useState<string | null>(null);
  const [spxCostsLoaded, setSpxCostsLoaded] = useState(false);
  const [spxCostRange, setSpxCostRange] = useState<string>('30');

  // Index config state
  const [indexMinCredit, setIndexMinCredit] = useState<number>(3);
  const [indexMinCreditInput, setIndexMinCreditInput] = useState<string>('3');
  const [indexConfigLoading, setIndexConfigLoading] = useState(false);
  const [indexConfigLoaded, setIndexConfigLoaded] = useState(false);
  const [indexConfigSaving, setIndexConfigSaving] = useState(false);
  const [indexConfigError, setIndexConfigError] = useState<string | null>(null);
  const [indexConfigUpdatedAt, setIndexConfigUpdatedAt] = useState<string | null>(null);

  // Index costs state
  const [indexCostSummary, setIndexCostSummary] = useState<SpxCostSummary | null>(null);
  const [indexDailyCosts, setIndexDailyCosts] = useState<SpxDailyCost[]>([]);
  const [indexCostsLoading, setIndexCostsLoading] = useState(false);
  const [indexCostsError, setIndexCostsError] = useState<string | null>(null);
  const [indexCostsLoaded, setIndexCostsLoaded] = useState(false);
  const [indexCostRange, setIndexCostRange] = useState<string>('30');

  // Billing audit state
  const [auditEvents, setAuditEvents] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [auditEmailFilter, setAuditEmailFilter] = useState('');
  const [auditEventFilter, setAuditEventFilter] = useState('');
  const [auditProductFilter, setAuditProductFilter] = useState('');

  const handle401 = useCallback((err: any) => {
    if (err.response?.status === 401) {
      adminService.clearToken();
      navigate('/admin-login');
      return true;
    }
    return false;
  }, [navigate]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const data = await adminService.getUsers();
      setAllUsers(data.users);
    } catch (err: any) {
      if (!handle401(err)) setUsersError('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [handle401]);

  // Fetch file logs
  const fetchLogs = useCallback(async (filters?: { fileKey?: string; email?: string }) => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const data = await adminService.getFileAccessLogs(filters);
      setLogs(data.logs);
      setLogUsers(data.users);
      setLogsLoaded(true);
    } catch (err: any) {
      if (!handle401(err)) setLogsError('Failed to load file access logs');
    } finally {
      setLogsLoading(false);
    }
  }, [handle401]);

  // Fetch support tickets
  const fetchTickets = useCallback(async (statusFilter?: string) => {
    setTicketsLoading(true);
    setTicketsError(null);
    try {
      const data = await adminService.getSupportTickets(statusFilter);
      setTickets(data.tickets);
      setTicketsLoaded(true);
    } catch (err: any) {
      if (!handle401(err)) setTicketsError('Failed to load support tickets');
    } finally {
      setTicketsLoading(false);
    }
  }, [handle401]);

  // Fetch SPX costs
  const fetchSpxCosts = useCallback(async (days?: number) => {
    setSpxCostsLoading(true);
    setSpxCostsError(null);
    try {
      const endDate = new Date().toISOString().slice(0, 10);
      const startD = new Date();
      startD.setDate(startD.getDate() - (days || 30));
      const startDate = startD.toISOString().slice(0, 10);
      const data = await adminService.getSpxCosts(startDate, endDate);
      setSpxCostSummary(data.summary);
      setSpxDailyCosts(data.daily);
    } catch (err: any) {
      if (!handle401(err)) setSpxCostsError('Failed to load SPX costs');
    } finally {
      setSpxCostsLoaded(true);
      setSpxCostsLoading(false);
    }
  }, [handle401]);

  const fetchIndexCosts = useCallback(async (days?: number) => {
    setIndexCostsLoading(true);
    setIndexCostsError(null);
    try {
      const endDate = new Date().toISOString().slice(0,10);
      const startD = new Date();
      startD.setDate(startD.getDate() - (days || 30));
      const startDate = startD.toISOString().slice(0, 10);
      const data = await adminService.getIndexCosts(startDate, endDate);
      setIndexCostSummary(data.summary);
      setIndexDailyCosts(data.daily);
    } catch (err: any) {
      if (!handle401(err)) setIndexCostsError('Failed to load index costs');
    } finally {
      setIndexCostsLoaded(true);
      setIndexCostsLoading(false);
    }
  }, [handle401]);

  useEffect(() => {
    if (!adminService.getToken()) {
      navigate('/admin-login');
      return;
    }
    fetchUsers();
  }, [navigate, fetchUsers]);

  // Re-fetch the user list whenever the tab regains focus, so the admin
  // doesn't act on stale data after DDB-side changes (test scripts, other
  // admins, cascade writes from adminUpdateUser).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && adminService.getToken()) {
        fetchUsers();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchUsers]);

  // Load logs on first tab switch
  useEffect(() => {
    if (activeTab === 'file-logs' && !logsLoaded && !logsLoading) {
      fetchLogs();
    }
  }, [activeTab, logsLoaded, logsLoading, fetchLogs]);

  // Load tickets on first tab switch
  useEffect(() => {
    if (activeTab === 'support-tickets' && !ticketsLoaded && !ticketsLoading) {
      fetchTickets();
    }
  }, [activeTab, ticketsLoaded, ticketsLoading, fetchTickets]);

  // Load SPX costs on first tab switch
  useEffect(() => {
    if (activeTab === 'spx-costs' && !spxCostsLoaded && !spxCostsLoading) {
      fetchSpxCosts(parseInt(spxCostRange));
    }
  }, [activeTab, spxCostsLoaded, spxCostsLoading, fetchSpxCosts, spxCostRange]);

  // Load SPX config on first tab switch
  useEffect(() => {
    if (activeTab === 'spx-config' && !spxConfigLoaded && !spxConfigLoading) {
      setSpxConfigLoading(true);
      setSpxConfigError(null);
      adminService.getSpxConfig()
        .then((data) => {
          setSpxMinCredit(data.minCredit);
          setSpxMinCreditInput(String(data.minCredit));
          setSpxConfigUpdatedAt(data.updatedAt);
        })
        .catch((err: any) => {
          if (!handle401(err)) setSpxConfigError('Failed to load SPX config');
        })
        .finally(() => { setSpxConfigLoaded(true); setSpxConfigLoading(false); });
    }
  }, [activeTab, spxConfigLoaded, spxConfigLoading, handle401]);

  // Load Index costs on first tab switch
  useEffect(() => {
    if (activeTab === 'index-costs' && !indexCostsLoaded && !indexCostsLoading) {
      fetchIndexCosts(parseInt(indexCostRange));
    }
  }, [activeTab, indexCostsLoaded, indexCostsLoading, fetchIndexCosts, indexCostRange]);

  // Load Index config on first tab switch
  useEffect(() => {
    if (activeTab === 'index-config' && !indexConfigLoaded && !indexConfigLoading) {
      setIndexConfigLoading(true);
      setIndexConfigError(null);
      adminService.getIndexConfig('SPY')
        .then((data: any) => {
          setIndexMinCredit(data.minCredit);
          setIndexMinCreditInput(String(data.minCredit));
          setIndexConfigUpdatedAt(data.updatedAt);
        })
        .catch((err: any) => {
          if (!handle401(err)) setIndexConfigError('Failed to load index config');
        })
        .finally(() => { setIndexConfigLoaded(true); setIndexConfigLoading(false); });
    }
  }, [activeTab, indexConfigLoaded, indexConfigLoading, handle401]);

  // Load billing audit on first tab switch
  useEffect(() => {
    if (activeTab === 'billing-audit' && !auditLoaded && !auditLoading) {
      fetchBillingAudit();
    }
  }, [activeTab, auditLoaded, auditLoading]);

  const fetchBillingAudit = async (filters?: { email?: string; event?: string; product?: string }) => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const data = await adminService.getBillingAudit({
        ...filters,
        limit: 200,
      });
      setAuditEvents(data.events || []);
      setAuditLoaded(true);
    } catch (err: any) {
      if (err?.response?.status === 401) handle401(err);
      setAuditError(err?.response?.data?.error || 'Failed to load billing audit');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleAuditFilter = () => {
    const filters: any = {};
    if (auditEmailFilter.trim()) filters.email = auditEmailFilter.trim();
    if (auditEventFilter) filters.event = auditEventFilter;
    if (auditProductFilter) filters.product = auditProductFilter;
    fetchBillingAudit(filters);
  };

  const handleAuditClear = () => {
    setAuditEmailFilter('');
    setAuditEventFilter('');
    setAuditProductFilter('');
    fetchBillingAudit();
  };

  const handleLogout = () => {
    adminService.clearToken();
    navigate('/admin-login');
  };

  // File logs filters
  const handleFilterApply = () => {
    const filters: { fileKey?: string; email?: string } = {};
    if (fileFilter) filters.fileKey = fileFilter;
    if (emailSearch.trim()) filters.email = emailSearch.trim();
    fetchLogs(filters);
  };

  const handleClearFilters = () => {
    setFileFilter('');
    setEmailSearch('');
    fetchLogs();
  };

  // Ticket management
  const handleTicketFilterApply = () => {
    fetchTickets(ticketStatusFilter || undefined);
  };

  const handleTicketClearFilters = () => {
    setTicketStatusFilter('');
    setTicketEmailSearch('');
    fetchTickets();
  };

  const handleTicketStatusChange = async (ticket: SupportTicket, newStatus: string) => {
    setTicketStatusUpdating(true);
    try {
      await adminService.updateSupportTicket(ticket.email, ticket.caseNumber, { status: newStatus });
      setSuccessMsg(`Ticket ${ticket.caseNumber} status updated to ${newStatus}`);
      if (selectedTicket?.caseNumber === ticket.caseNumber) {
        setSelectedTicket({ ...selectedTicket, status: newStatus, updatedAt: new Date().toISOString() });
      }
      fetchTickets(ticketStatusFilter || undefined);
    } catch (err: any) {
      if (!handle401(err)) alert('Failed to update ticket status');
    } finally {
      setTicketStatusUpdating(false);
    }
  };

  const handleSendReply = async () => {
    if (!selectedTicket || !replyText.trim()) return;
    setReplySending(true);
    try {
      await adminService.updateSupportTicket(selectedTicket.email, selectedTicket.caseNumber, { reply: replyText.trim() });
      const newReply: TicketReply = { from: 'admin', message: replyText.trim(), timestamp: new Date().toISOString() };
      const updatedReplies = [...(selectedTicket.replies || []), newReply];
      const newStatus = selectedTicket.status === 'open' ? 'in-progress' : selectedTicket.status;
      setSelectedTicket({ ...selectedTicket, replies: updatedReplies, status: newStatus, updatedAt: new Date().toISOString() });
      setReplyText('');
      setSuccessMsg(`Reply sent to ${selectedTicket.email}`);
      fetchTickets(ticketStatusFilter || undefined);
    } catch (err: any) {
      if (!handle401(err)) alert('Failed to send reply');
    } finally {
      setReplySending(false);
    }
  };

  const filteredTickets = ticketEmailSearch.trim()
    ? tickets.filter(t => t.email.toLowerCase().includes(ticketEmailSearch.toLowerCase()))
    : tickets;

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'open': return 'admin-badge-green';
      case 'in-progress': return 'admin-badge-amber';
      case 'resolved': return 'admin-badge-blue';
      case 'closed': return 'admin-badge-red';
      default: return '';
    }
  };

  // User management
  const openEdit = (user: UserInfo) => {
    setEditingUser(user);
    setEditFields({
      emailVerified: user.emailVerified ?? false,
      subscriptionStatus: user.subscriptionStatus || 'free',
      subscriptionTier: user.subscriptionTier || 'free',
      effectiveTier: user.effectiveTier || 'free',
      hourlyEmailsEnabled: user.hourlyEmailsEnabled ?? false,
      dailyReportEnabled: user.dailyReportEnabled ?? false,
      algoTradingEnabled: user.algoTradingEnabled ?? false,
      algoEngineLimit: user.algoEngineLimit ?? 1,
      optionsAutoTradingEnabled: user.optionsAutoTradingEnabled ?? false,
      tradingSnapshotEnabled: user.tradingSnapshotEnabled ?? false,
      spxSignalsEnabled: user.spxSignalsEnabled ?? false,
      spySignalsEnabled: (user as any).spySignalsEnabled ?? false,
      esSignalsEnabled: (user as any).esSignalsEnabled ?? false,
      mesSignalsEnabled: (user as any).mesSignalsEnabled ?? false,
      indexSignalsEnabled: user.indexSignalsEnabled ?? false,
      notchesEnabled: (user as any).notchesEnabled ?? false,
      showV1Signals: (user as any).showV1Signals ?? false,
    });
    setSuccessMsg(null);
  };

  const handleEditSave = async () => {
    if (!editingUser) return;
    setEditSaving(true);
    try {
      await adminService.updateUser(editingUser.email, editFields);
      setEditingUser(null);
      setSuccessMsg(`Updated ${editingUser.email}`);
      fetchUsers();
    } catch (err: any) {
      if (!handle401(err)) alert('Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (email: string) => {
    setDeleteLoading(true);
    try {
      await adminService.deleteUser(email);
      setDeleteConfirm(null);
      setSuccessMsg(`Deleted ${email}`);
      fetchUsers();
    } catch (err: any) {
      if (!handle401(err)) alert('Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Trial management
  const openTrialModal = (user: UserInfo) => {
    setTrialUser(user);
    setTrialTier('premier');
    setTrialDuration('7');
    setTrialCustomDate('');
    setSuccessMsg(null);
  };

  const getTrialDaysRemaining = (user: UserInfo): number | null => {
    if (!user.complimentaryTier || !user.complimentaryExpiresAt) return null;
    const remaining = Math.ceil((new Date(user.complimentaryExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return remaining;
  };

  const handleGrantTrial = async () => {
    if (!trialUser) return;
    setTrialSaving(true);
    try {
      let expiresAt: string;
      if (trialDuration === 'custom') {
        if (!trialCustomDate) { setTrialSaving(false); return; }
        expiresAt = new Date(trialCustomDate + 'T23:59:59Z').toISOString();
      } else {
        const d = new Date();
        d.setDate(d.getDate() + parseInt(trialDuration));
        expiresAt = d.toISOString();
      }
      await adminService.updateUser(trialUser.email, {
        complimentaryTier: trialTier,
        complimentaryExpiresAt: expiresAt,
      });
      setTrialUser(null);
      setSuccessMsg(`Granted ${trialTier} trial to ${trialUser.email}`);
      fetchUsers();
    } catch (err: any) {
      if (!handle401(err)) alert('Failed to grant trial');
    } finally {
      setTrialSaving(false);
    }
  };

  const handleRevokeTrial = async () => {
    if (!trialUser) return;
    setTrialSaving(true);
    try {
      await adminService.updateUser(trialUser.email, {
        complimentaryTier: '',
        complimentaryExpiresAt: '',
      });
      setTrialUser(null);
      setSuccessMsg(`Revoked trial for ${trialUser.email}`);
      fetchUsers();
    } catch (err: any) {
      if (!handle401(err)) alert('Failed to revoke trial');
    } finally {
      setTrialSaving(false);
    }
  };

  // File logs computed values
  const totalDownloads = logs.length;
  const uniqueDownloadUsers = new Set(logs.map(l => l.email)).size;
  const fileCounts: Record<string, number> = {};
  logs.forEach(l => { fileCounts[l.fileKey] = (fileCounts[l.fileKey] || 0) + 1; });
  const mostPopularFile = Object.entries(fileCounts).sort((a, b) => b[1] - a[1])[0];
  const uniqueFileKeys = Array.from(new Set(logs.map(l => l.fileKey))).sort();

  // Users computed / filtered
  const activeTrials = allUsers.filter(u => {
    const days = getTrialDaysRemaining(u);
    return days !== null && days > 0;
  }).length;

  const renderTrialBadge = (user: UserInfo) => {
    const days = getTrialDaysRemaining(user);
    if (days === null) return '-';
    if (days > 0) {
      return <span className="admin-badge admin-badge-amber">{user.complimentaryTier} ({days}d)</span>;
    }
    return <span className="admin-badge admin-badge-red">expired</span>;
  };

  const filteredUsers = userSearch.trim()
    ? allUsers.filter(u => u.email.toLowerCase().includes(userSearch.toLowerCase()))
    : allUsers;

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const truncateUA = (ua?: string) => {
    if (!ua || ua === 'unknown') return '-';
    if (ua.length > 60) return ua.substring(0, 57) + '...';
    return ua;
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <span className="admin-header-icon">&#x1F6E1;</span>
          <h1>Admin Dashboard</h1>
        </div>
        <button className="admin-logout-btn" onClick={handleLogout}>Logout</button>
      </header>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users ({allUsers.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'support-tickets' ? 'active' : ''}`}
          onClick={() => setActiveTab('support-tickets')}
        >
          Support Tickets ({tickets.length})
        </button>
        <button
          className={`admin-tab ${activeTab === 'file-logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('file-logs')}
        >
          File Access Logs
        </button>
        <button
          className={`admin-tab ${activeTab === 'spx-costs' ? 'active' : ''}`}
          onClick={() => setActiveTab('spx-costs')}
        >
          SPX AI Costs
        </button>
        <button
          className={`admin-tab ${activeTab === 'spx-config' ? 'active' : ''}`}
          onClick={() => setActiveTab('spx-config')}
        >
          SPX Config
        </button>
        <button
          className={`admin-tab ${activeTab === 'index-costs' ? 'active' : ''}`}
          onClick={() => setActiveTab('index-costs')}
        >
          Index AI Costs
        </button>
        <button
          className={`admin-tab ${activeTab === 'index-config' ? 'active' : ''}`}
          onClick={() => setActiveTab('index-config')}
        >
          Index Config
        </button>
        <button
          className={`admin-tab ${activeTab === 'billing-audit' ? 'active' : ''}`}
          onClick={() => setActiveTab('billing-audit')}
        >
          Billing Audit
        </button>
        <button
          className={`admin-tab ${activeTab === 'regime' ? 'active' : ''}`}
          onClick={() => setActiveTab('regime')}
        >
          Regime
        </button>
        <button
          className={`admin-tab ${activeTab === 'trade-signals-config' ? 'active' : ''}`}
          onClick={() => setActiveTab('trade-signals-config')}
        >
          Trade Signals Config
        </button>
        <button
          className={`admin-tab ${activeTab === 'page-view-stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('page-view-stats')}
        >
          Study Page Clicks
        </button>
        <button
          className={`admin-tab ${activeTab === 'calendar-notches' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar-notches')}
        >
          Calendar Notches
        </button>
        <button
          className={`admin-tab ${activeTab === 'briefing-shadow' ? 'active' : ''}`}
          onClick={() => setActiveTab('briefing-shadow')}
        >
          Briefing Shadow
        </button>
      </div>

      <div className="admin-content">
        {successMsg && (
          <div className="admin-success">{successMsg}</div>
        )}

        {/* ===== USERS TAB ===== */}
        {activeTab === 'users' && (
          <>
            {/* Users stats */}
            <div className="admin-stats-row">
              <div className="admin-stat-card">
                <div className="admin-stat-value">{allUsers.length}</div>
                <div className="admin-stat-label">Total Users</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{allUsers.filter(u => u.emailVerified).length}</div>
                <div className="admin-stat-label">Verified</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{allUsers.filter(u => (u.effectiveTier || u.subscriptionTier || 'free') !== 'free').length}</div>
                <div className="admin-stat-label">Paid Tier</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{activeTrials}</div>
                <div className="admin-stat-label">Active Trials</div>
              </div>
            </div>

            {/* Search */}
            <div className="admin-filters">
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by email..."
                className="admin-search-input"
              />
              <button
                type="button"
                onClick={fetchUsers}
                disabled={usersLoading}
                className="admin-action-btn"
                style={{ marginLeft: 8 }}
                title="Re-fetch the user list from DynamoDB"
              >
                {usersLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {usersError && <div className="admin-error">{usersError}</div>}

            {usersLoading ? (
              <div className="admin-loading">Loading users...</div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Verified</th>
                      <th>Tier</th>
                      <th>Status</th>
                      <th>Trial</th>
                      <th>Hourly</th>
                      <th>Daily</th>
                      <th>Algo</th>
                      <th>SPX</th>
                      <th>Index</th>
                      <th>Registered</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr><td colSpan={10} className="admin-empty">No users found</td></tr>
                    ) : (
                      filteredUsers.map(user => (
                        <tr key={user.email}>
                          <td>{user.email}</td>
                          <td>
                            <span className={`admin-badge ${user.emailVerified ? 'admin-badge-green' : 'admin-badge-red'}`}>
                              {user.emailVerified ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>{user.effectiveTier || user.subscriptionTier || 'free'}</td>
                          <td>{user.subscriptionStatus || 'free'}</td>
                          <td>{renderTrialBadge(user)}</td>
                          <td>{user.hourlyEmailsEnabled ? 'On' : 'Off'}</td>
                          <td>{user.dailyReportEnabled ? 'On' : 'Off'}</td>
                          <td>
                            {user.algoTradingEnabled
                              ? <span className="admin-badge admin-badge-green">On ({user.algoEngineLimit ?? 1})</span>
                              : <span className="admin-badge">Off</span>}
                            {user.optionsAutoTradingEnabled
                              ? <span className="admin-badge admin-badge-green" style={{marginLeft: 4}}>Opts</span>
                              : null}
                          </td>
                          <td>
                            {user.spxSignalsEnabled
                              ? <span className="admin-badge admin-badge-green">On</span>
                              : <span className="admin-badge">Off</span>}
                          </td>
                          <td>
                            {user.indexSignalsEnabled
                              ? <span className="admin-badge admin-badge-green">On</span>
                              : <span className="admin-badge">Off</span>}
                          </td>
                          <td>{formatDate(user.createdAt)}</td>
                          <td>{formatDate(user.lastLogin)}</td>
                          <td className="admin-actions-cell">
                            <button className="admin-action-btn admin-edit-btn" onClick={() => openEdit(user)}>Edit</button>
                            <button className="admin-action-btn admin-trial-btn" onClick={() => openTrialModal(user)}>Trial</button>
                            <button className="admin-action-btn admin-delete-btn" onClick={() => setDeleteConfirm(user.email)}>Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Edit Modal */}
            {editingUser && (
              <div className="admin-modal-overlay" onClick={() => setEditingUser(null)}>
                <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                  <h3>Edit User</h3>
                  <p className="admin-modal-email">{editingUser.email}</p>

                  <div className="admin-modal-field">
                    <label>Email Verified</label>
                    <select value={String(editFields.emailVerified)} onChange={(e) => setEditFields({...editFields, emailVerified: e.target.value === 'true'})}>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Subscription Status</label>
                    <select value={editFields.subscriptionStatus} onChange={(e) => setEditFields({...editFields, subscriptionStatus: e.target.value})}>
                      <option value="free">free</option>
                      <option value="active">active</option>
                      <option value="cancelled">cancelled</option>
                      <option value="expired">expired</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Subscription Tier</label>
                    <select value={editFields.subscriptionTier} onChange={(e) => setEditFields({...editFields, subscriptionTier: e.target.value})}>
                      <option value="free">free</option>
                      <option value="basic">basic</option>
                      <option value="premier">premier</option>
                      <option value="pro">pro</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Effective Tier</label>
                    <select value={editFields.effectiveTier} onChange={(e) => setEditFields({...editFields, effectiveTier: e.target.value})}>
                      <option value="free">free</option>
                      <option value="basic">basic</option>
                      <option value="premier">premier</option>
                      <option value="pro">pro</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Hourly Emails</label>
                    <select value={String(editFields.hourlyEmailsEnabled)} onChange={(e) => setEditFields({...editFields, hourlyEmailsEnabled: e.target.value === 'true'})}>
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Daily Report</label>
                    <select value={String(editFields.dailyReportEnabled)} onChange={(e) => setEditFields({...editFields, dailyReportEnabled: e.target.value === 'true'})}>
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Algo Trading</label>
                    <select value={String(editFields.algoTradingEnabled)} onChange={(e) => setEditFields({...editFields, algoTradingEnabled: e.target.value === 'true'})}>
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Algo Engine Limit</label>
                    <select value={String(editFields.algoEngineLimit)} onChange={(e) => setEditFields({...editFields, algoEngineLimit: parseInt(e.target.value)})}>
                      <option value="1">1 engine</option>
                      <option value="2">2 engines</option>
                      <option value="3">3 engines</option>
                      <option value="5">5 engines</option>
                      <option value="10">10 engines</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Options Auto-Trader</label>
                    <select value={String(editFields.optionsAutoTradingEnabled)} onChange={(e) => setEditFields({...editFields, optionsAutoTradingEnabled: e.target.value === 'true'})}>
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Trading Snapshot (daily email)</label>
                    <select value={String(editFields.tradingSnapshotEnabled)} onChange={(e) => setEditFields({...editFields, tradingSnapshotEnabled: e.target.value === 'true'})}>
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>SPX Signals</label>
                    <select value={String(editFields.spxSignalsEnabled)} onChange={(e) => setEditFields({...editFields, spxSignalsEnabled: e.target.value === 'true'})}>
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Index Signals (legacy)</label>
                    <select value={String(editFields.indexSignalsEnabled)} onChange={(e) => setEditFields({...editFields, indexSignalsEnabled: e.target.value === 'true'})}>
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>SPY Signals</label>
                    <select value={String((editFields as any).spySignalsEnabled)} onChange={(e) => setEditFields({...editFields, spySignalsEnabled: e.target.value === 'true'} as any)}>
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>ES Signals</label>
                    <select value={String((editFields as any).esSignalsEnabled)} onChange={(e) => setEditFields({...editFields, esSignalsEnabled: e.target.value === 'true'} as any)}>
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>MES Signals</label>
                    <select value={String((editFields as any).mesSignalsEnabled)} onChange={(e) => setEditFields({...editFields, mesSignalsEnabled: e.target.value === 'true'} as any)}>
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Notches (v1, parallel)</label>
                    <select value={String((editFields as any).notchesEnabled)} onChange={(e) => setEditFields({...editFields, notchesEnabled: e.target.value === 'true'} as any)}>
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Show V1 Signals</label>
                    <select value={String((editFields as any).showV1Signals)} onChange={(e) => setEditFields({...editFields, showV1Signals: e.target.value === 'true'} as any)}>
                      <option value="false">Hidden</option>
                      <option value="true">Visible</option>
                    </select>
                  </div>

                  <div className="admin-modal-actions">
                    <button className="admin-btn-filter" onClick={handleEditSave} disabled={editSaving}>
                      {editSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button className="admin-btn-clear" onClick={() => setEditingUser(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
              <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
                <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
                  <h3>Delete User</h3>
                  <p className="admin-modal-warn">Are you sure you want to permanently delete <strong>{deleteConfirm}</strong>? This cannot be undone.</p>
                  <div className="admin-modal-actions">
                    <button className="admin-action-btn admin-delete-btn" onClick={() => handleDelete(deleteConfirm)} disabled={deleteLoading}>
                      {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                    <button className="admin-btn-clear" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Trial Modal */}
            {trialUser && (
              <div className="admin-modal-overlay" onClick={() => setTrialUser(null)}>
                <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                  <h3>Manage Trial</h3>
                  <p className="admin-modal-email">{trialUser.email}</p>

                  {trialUser.complimentaryTier && trialUser.complimentaryExpiresAt && (
                    <div className="admin-trial-status">
                      {(() => {
                        const days = getTrialDaysRemaining(trialUser);
                        if (days !== null && days > 0) {
                          return <>Current trial: <strong>{trialUser.complimentaryTier}</strong> — {days} day{days !== 1 ? 's' : ''} remaining</>;
                        }
                        return <>Previous trial: <strong>{trialUser.complimentaryTier}</strong> — <span style={{color: '#fca5a5'}}>expired</span></>;
                      })()}
                    </div>
                  )}

                  <div className="admin-modal-field">
                    <label>Tier</label>
                    <select value={trialTier} onChange={(e) => setTrialTier(e.target.value)}>
                      <option value="premier">premier</option>
                      <option value="pro">pro</option>
                    </select>
                  </div>

                  <div className="admin-modal-field">
                    <label>Duration</label>
                    <div className="admin-duration-presets">
                      {['7', '14', '30'].map(d => (
                        <button
                          key={d}
                          className={trialDuration === d ? 'active' : ''}
                          onClick={() => { setTrialDuration(d); setTrialCustomDate(''); }}
                        >
                          {d} days
                        </button>
                      ))}
                      <button
                        className={trialDuration === 'custom' ? 'active' : ''}
                        onClick={() => setTrialDuration('custom')}
                      >
                        Custom
                      </button>
                    </div>
                  </div>

                  {trialDuration === 'custom' && (
                    <div className="admin-modal-field">
                      <label>Expires On</label>
                      <input
                        type="date"
                        value={trialCustomDate}
                        onChange={(e) => setTrialCustomDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  )}

                  <div className="admin-modal-actions">
                    <button className="admin-btn-filter" onClick={handleGrantTrial} disabled={trialSaving}>
                      {trialSaving ? 'Saving...' : 'Grant Trial'}
                    </button>
                    {trialUser.complimentaryTier && getTrialDaysRemaining(trialUser) !== null && (getTrialDaysRemaining(trialUser) ?? 0) > 0 && (
                      <button className="admin-btn-revoke" onClick={handleRevokeTrial} disabled={trialSaving}>
                        Revoke Trial
                      </button>
                    )}
                    <button className="admin-btn-clear" onClick={() => setTrialUser(null)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== SUPPORT TICKETS TAB ===== */}
        {activeTab === 'support-tickets' && (
          <>
            {/* Ticket stats */}
            <div className="admin-stats-row">
              <div className="admin-stat-card">
                <div className="admin-stat-value">{tickets.length}</div>
                <div className="admin-stat-label">Total Tickets</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{tickets.filter(t => t.status === 'open').length}</div>
                <div className="admin-stat-label">Open</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{tickets.filter(t => t.status === 'in-progress').length}</div>
                <div className="admin-stat-label">In Progress</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{tickets.filter(t => t.status === 'resolved').length}</div>
                <div className="admin-stat-label">Resolved</div>
              </div>
            </div>

            {/* Filters */}
            <div className="admin-filters">
              <select
                value={ticketStatusFilter}
                onChange={(e) => setTicketStatusFilter(e.target.value)}
                className="admin-select"
              >
                <option value="">All statuses</option>
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <input
                type="text"
                value={ticketEmailSearch}
                onChange={(e) => setTicketEmailSearch(e.target.value)}
                placeholder="Filter by email..."
                className="admin-search-input"
                onKeyDown={(e) => e.key === 'Enter' && handleTicketFilterApply()}
              />
              <button className="admin-btn-filter" onClick={handleTicketFilterApply}>Apply</button>
              {(ticketStatusFilter || ticketEmailSearch) && (
                <button className="admin-btn-clear" onClick={handleTicketClearFilters}>Clear</button>
              )}
            </div>

            {ticketsError && <div className="admin-error">{ticketsError}</div>}

            {ticketsLoading ? (
              <div className="admin-loading">Loading tickets...</div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Case #</th>
                      <th>Email</th>
                      <th>Subject</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.length === 0 ? (
                      <tr><td colSpan={7} className="admin-empty">No tickets found</td></tr>
                    ) : (
                      filteredTickets.map(ticket => (
                        <tr key={`${ticket.email}-${ticket.caseNumber}`}>
                          <td><span className="admin-file-badge">{ticket.caseNumber}</span></td>
                          <td>{ticket.email}</td>
                          <td>{ticket.subject}</td>
                          <td>{ticket.category}</td>
                          <td>
                            <span className={`admin-badge ${getStatusBadgeClass(ticket.status)}`}>
                              {ticket.status}
                            </span>
                          </td>
                          <td>{formatDate(ticket.createdAt)}</td>
                          <td>
                            <button
                              className="admin-action-btn admin-edit-btn"
                              onClick={() => { setSelectedTicket(ticket); setReplyText(''); }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Ticket Detail Modal */}
            {selectedTicket && (
              <div className="admin-modal-overlay" onClick={() => setSelectedTicket(null)}>
                <div className="admin-modal admin-modal-lg" onClick={(e) => e.stopPropagation()}>
                  <h3>Ticket Detail</h3>
                  <p className="admin-modal-email">{selectedTicket.caseNumber} &mdash; {selectedTicket.email}</p>

                  <div className="admin-modal-field">
                    <label>Subject</label>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>{selectedTicket.subject}</div>
                  </div>

                  <div className="admin-modal-field">
                    <label>Category</label>
                    <span className="admin-badge" style={{ background: 'rgba(102,126,234,0.15)', color: '#93a8f4' }}>{selectedTicket.category}</span>
                  </div>

                  <div className="admin-modal-field">
                    <label>Description</label>
                    <div className="admin-ticket-description">{selectedTicket.description}</div>
                  </div>

                  <div className="admin-modal-field">
                    <label>Status</label>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => handleTicketStatusChange(selectedTicket, e.target.value)}
                      disabled={ticketStatusUpdating}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  {/* Reply thread */}
                  {selectedTicket.replies && selectedTicket.replies.length > 0 && (
                    <div className="admin-modal-field">
                      <label>Reply History</label>
                      <div className="admin-reply-thread">
                        {selectedTicket.replies.map((r, i) => (
                          <div key={i} className="admin-reply-item">
                            <div className="admin-reply-meta">
                              <strong>{r.from === 'admin' ? 'Admin' : r.from}</strong>
                              <span>{formatDate(r.timestamp)}</span>
                            </div>
                            <div className="admin-reply-message">{r.message}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reply input */}
                  <div className="admin-modal-field">
                    <label>Send Reply</label>
                    <textarea
                      className="admin-reply-textarea"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      rows={4}
                    />
                  </div>

                  <div className="admin-modal-actions">
                    <button
                      className="admin-btn-filter"
                      onClick={handleSendReply}
                      disabled={replySending || !replyText.trim()}
                    >
                      {replySending ? 'Sending...' : 'Send Reply'}
                    </button>
                    <button className="admin-btn-clear" onClick={() => setSelectedTicket(null)}>Close</button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== SPX AI COSTS TAB ===== */}
        {activeTab === 'spx-costs' && (
          <>
            {/* Range selector */}
            <div className="admin-filters">
              <select
                value={spxCostRange}
                onChange={(e) => {
                  setSpxCostRange(e.target.value);
                  setSpxCostsLoaded(false);
                }}
                className="admin-select"
              >
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
                <option value="30">Last 30 days</option>
                <option value="60">Last 60 days</option>
                <option value="90">Last 90 days</option>
              </select>
              <button className="admin-btn-filter" onClick={() => { setSpxCostsLoaded(false); }}>
                Refresh
              </button>
            </div>

            {spxCostsError && <div className="admin-error">{spxCostsError}</div>}

            {spxCostsLoading ? (
              <div className="admin-loading">Loading SPX costs...</div>
            ) : spxCostSummary ? (
              <>
                {/* Summary stats */}
                <div className="admin-stats-row">
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${spxCostSummary.total_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">Total AI Cost</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${spxCostSummary.claude_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">Claude</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${spxCostSummary.chatgpt_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">ChatGPT</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${spxCostSummary.avg_per_day_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">Avg/Day</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${spxCostSummary.projected_monthly_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">Proj. Monthly</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">{spxCostSummary.total_snapshots}</div>
                    <div className="admin-stat-label">Snapshots</div>
                  </div>
                </div>

                {/* Token usage summary */}
                <div style={{ margin: '16px 0', padding: '12px 16px', background: 'rgba(102,126,234,0.08)', borderRadius: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                  <strong style={{ color: 'rgba(255,255,255,0.9)' }}>Token Usage</strong>
                  <span style={{ margin: '0 16px' }}>
                    Claude: {spxCostSummary.tokens.claude_input.toLocaleString()} in / {spxCostSummary.tokens.claude_output.toLocaleString()} out
                  </span>
                  <span>
                    ChatGPT: {spxCostSummary.tokens.chatgpt_input.toLocaleString()} in / {spxCostSummary.tokens.chatgpt_output.toLocaleString()} out
                  </span>
                </div>

                {/* Daily breakdown table */}
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Snapshots</th>
                        <th>Claude</th>
                        <th>ChatGPT</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {spxDailyCosts.length === 0 ? (
                        <tr><td colSpan={5} className="admin-empty">No cost data for this period</td></tr>
                      ) : (
                        [...spxDailyCosts].reverse().map(day => (
                          <tr key={day.date}>
                            <td>{day.date}</td>
                            <td>{day.snapshots}</td>
                            <td>${day.claude_usd.toFixed(4)}</td>
                            <td>${day.chatgpt_usd.toFixed(4)}</td>
                            <td><strong>${day.total_usd.toFixed(4)}</strong></td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </>
        )}

        {/* ===== SPX CONFIG TAB ===== */}
        {activeTab === 'spx-config' && (
          <>
            {spxConfigError && <div className="admin-error">{spxConfigError}</div>}

            {spxConfigLoading ? (
              <div className="admin-loading">Loading SPX config...</div>
            ) : (
              <div style={{ maxWidth: '480px' }}>
                <h3 style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '16px' }}>Signal Generation Settings</h3>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '6px', fontWeight: 600 }}>
                    Minimum Credit ($)
                  </label>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' }}>
                    Only consider put candidates with credit per point &ge; this amount (e.g., $30 = $3,000/contract).
                  </p>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={spxMinCreditInput}
                      onChange={(e) => setSpxMinCreditInput(e.target.value)}
                      min={0}
                      max={500}
                      step={5}
                      className="admin-input"
                      style={{ width: '120px' }}
                    />
                    <button
                      className="admin-btn-filter"
                      disabled={spxConfigSaving || parseFloat(spxMinCreditInput) === spxMinCredit}
                      onClick={async () => {
                        const val = parseFloat(spxMinCreditInput);
                        if (isNaN(val) || val < 0 || val > 500) {
                          setSpxConfigError('Value must be between 0 and 500');
                          return;
                        }
                        setSpxConfigSaving(true);
                        setSpxConfigError(null);
                        try {
                          await adminService.updateSpxConfig({ minCredit: val });
                          setSpxMinCredit(val);
                          setSpxConfigUpdatedAt(new Date().toISOString());
                          setSuccessMsg('SPX min credit updated to $' + val);
                          setTimeout(() => setSuccessMsg(null), 3000);
                        } catch (err: any) {
                          if (!handle401(err)) setSpxConfigError(err.response?.data?.error || 'Failed to update');
                        } finally {
                          setSpxConfigSaving(false);
                        }
                      }}
                    >
                      {spxConfigSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '6px' }}>
                    Current: <strong>${spxMinCredit}</strong>
                    {spxConfigUpdatedAt && <span> &middot; Last updated: {new Date(spxConfigUpdatedAt).toLocaleString()}</span>}
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== INDEX COSTS TAB ===== */}
        {activeTab === 'index-costs' && (
          <>
            <div className="admin-filters">
              <select
                value={indexCostRange}
                onChange={(e) => {
                  setIndexCostRange(e.target.value);
                  setIndexCostsLoaded(false);
                }}
                className="admin-select"
              >
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
                <option value="30">Last 30 days</option>
                <option value="60">Last 60 days</option>
                <option value="90">Last 90 days</option>
              </select>
              <button className="admin-btn-filter" onClick={() => { setIndexCostsLoaded(false); }}>
                Refresh
              </button>
            </div>

            {indexCostsError && <div className="admin-error">{indexCostsError}</div>}

            {indexCostsLoading ? (
              <div className="admin-loading">Loading index costs...</div>
            ) : indexCostSummary ? (
              <>
                <div className="admin-stats-row">
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${indexCostSummary.total_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">Total Cost</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${indexCostSummary.claude_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">Claude</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${indexCostSummary.chatgpt_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">ChatGPT</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${indexCostSummary.avg_per_day_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">Avg/Day</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">${indexCostSummary.projected_monthly_usd.toFixed(2)}</div>
                    <div className="admin-stat-label">Proj. Monthly</div>
                  </div>
                  <div className="admin-stat-card">
                    <div className="admin-stat-value">{indexCostSummary.total_snapshots}</div>
                    <div className="admin-stat-label">Snapshots</div>
                  </div>
                </div>

                <table className="admin-table" style={{ marginTop: '16px' }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Claude ($)</th>
                      <th>ChatGPT ($)</th>
                      <th>Total ($)</th>
                      <th>Snapshots</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indexDailyCosts.map(d => (
                      <tr key={d.date}>
                        <td>{d.date}</td>
                        <td>${d.claude_usd.toFixed(4)}</td>
                        <td>${d.chatgpt_usd.toFixed(4)}</td>
                        <td><strong>${d.total_usd.toFixed(4)}</strong></td>
                        <td>{d.snapshots}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.5)' }}>No index cost data available</div>
            )}
          </>
        )}

        {/* ===== INDEX CONFIG TAB ===== */}
        {activeTab === 'index-config' && (
          <>
            {indexConfigError && <div className="admin-error">{indexConfigError}</div>}

            {indexConfigLoading ? (
              <div className="admin-loading">Loading index config...</div>
            ) : (
              <div style={{ maxWidth: '480px' }}>
                <h3 style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '16px' }}>Index Signal Generation Settings (SPY)</h3>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', marginBottom: '6px', fontWeight: 600 }}>
                    Minimum Credit ($)
                  </label>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' }}>
                    Only consider put candidates with credit per point &ge; this amount.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={indexMinCreditInput}
                      onChange={(e) => setIndexMinCreditInput(e.target.value)}
                      min={0}
                      max={500}
                      step={1}
                      className="admin-input"
                      style={{ width: '120px' }}
                    />
                    <button
                      className="admin-btn-filter"
                      disabled={indexConfigSaving || parseFloat(indexMinCreditInput) === indexMinCredit}
                      onClick={async () => {
                        const val = parseFloat(indexMinCreditInput);
                        if (isNaN(val) || val < 0 || val > 500) {
                          setIndexConfigError('Value must be between 0 and 500');
                          return;
                        }
                        setIndexConfigSaving(true);
                        setIndexConfigError(null);
                        try {
                          await adminService.updateIndexConfig({ minCredit: val, symbol: 'SPY' });
                          setIndexMinCredit(val);
                          setIndexConfigUpdatedAt(new Date().toISOString());
                          setSuccessMsg('Index min credit updated to $' + val);
                          setTimeout(() => setSuccessMsg(null), 3000);
                        } catch (err: any) {
                          if (!handle401(err)) setIndexConfigError(err.response?.data?.error || 'Failed to update');
                        } finally {
                          setIndexConfigSaving(false);
                        }
                      }}
                    >
                      {indexConfigSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '6px' }}>
                    Current: <strong>${indexMinCredit}</strong>
                    {indexConfigUpdatedAt && <span> &middot; Last updated: {new Date(indexConfigUpdatedAt).toLocaleString()}</span>}
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== BILLING AUDIT TAB ===== */}
        {activeTab === 'billing-audit' && (
          <>
            {/* Filters */}
            <div className="admin-filter-bar" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Filter by email..."
                value={auditEmailFilter}
                onChange={(e) => setAuditEmailFilter(e.target.value)}
                className="admin-input"
                style={{ width: '220px' }}
              />
              <select value={auditEventFilter} onChange={(e) => setAuditEventFilter(e.target.value)} className="admin-input" style={{ width: '160px' }}>
                <option value="">All Events</option>
                <option value="activated">Activated</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
                <option value="webhook_activated">Webhook Activated</option>
                <option value="webhook_cancelled">Webhook Cancelled</option>
                <option value="webhook_payment">Webhook Payment</option>
                <option value="webhook_suspended">Webhook Suspended</option>
                <option value="webhook_expired">Webhook Expired</option>
                <option value="webhook_refunded">Webhook Refunded</option>
              </select>
              <select value={auditProductFilter} onChange={(e) => setAuditProductFilter(e.target.value)} className="admin-input" style={{ width: '140px' }}>
                <option value="">All Products</option>
                <option value="premier">Premier</option>
                <option value="spx">SPX</option>
                <option value="spy">SPY</option>
                <option value="es">ES</option>
                <option value="mes">MES</option>
                <option value="algo">Algo</option>
                <option value="bundle">Bundle</option>
              </select>
              <button className="admin-btn-filter" onClick={handleAuditFilter}>Search</button>
              <button className="admin-btn-filter" onClick={handleAuditClear} style={{ opacity: 0.7 }}>Clear</button>
            </div>

            {auditError && <div className="admin-error">{auditError}</div>}

            {auditLoading ? (
              <div className="admin-loading">Loading billing audit...</div>
            ) : auditEvents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.5)' }}>
                No billing events found. Events will appear here when users subscribe, cancel, or get refunds.
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '12px', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                  Showing {auditEvents.length} event{auditEvents.length !== 1 ? 's' : ''}
                </div>
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Time (ET)</th>
                        <th>Email</th>
                        <th>Event</th>
                        <th>Product</th>
                        <th>Amount</th>
                        <th>Source</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditEvents.map((evt: any, idx: number) => {
                        const ts = evt.timestamp ? evt.timestamp.split('#')[0] : '';
                        const timeStr = ts ? new Date(ts).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
                        const eventColor = evt.event?.includes('cancel') ? '#f87171'
                          : evt.event?.includes('refund') ? '#fbbf24'
                          : evt.event?.includes('activated') ? '#4ade80'
                          : evt.event?.includes('payment') ? '#60a5fa'
                          : evt.event?.includes('suspend') ? '#f97316'
                          : evt.event?.includes('expired') ? '#94a3b8'
                          : '#e2e8f0';
                        return (
                          <tr key={idx}>
                            <td style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>{timeStr}</td>
                            <td style={{ fontSize: '13px' }}>{evt.email || '-'}</td>
                            <td>
                              <span style={{
                                display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
                                fontSize: '12px', fontWeight: 600,
                                backgroundColor: `${eventColor}20`, color: eventColor,
                                border: `1px solid ${eventColor}40`,
                              }}>
                                {evt.event || '-'}
                              </span>
                            </td>
                            <td style={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '13px' }}>{evt.product || '-'}</td>
                            <td style={{ fontSize: '13px' }}>{evt.amount ? `$${evt.amount}` : '-'}</td>
                            <td style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>{evt.source || '-'}</td>
                            <td style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {evt.paypalSubscriptionId || evt.reason || (evt.metadata ? JSON.stringify(evt.metadata) : '-')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {/* ===== FILE LOGS TAB ===== */}
        {activeTab === 'file-logs' && (
          <>
            <div className="admin-stats-row">
              <div className="admin-stat-card">
                <div className="admin-stat-value">{totalDownloads}</div>
                <div className="admin-stat-label">Total Downloads</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{uniqueDownloadUsers}</div>
                <div className="admin-stat-label">Unique Users</div>
              </div>
              <div className="admin-stat-card">
                <div className="admin-stat-value">{mostPopularFile ? mostPopularFile[0] : '-'}</div>
                <div className="admin-stat-label">Most Popular ({mostPopularFile ? mostPopularFile[1] : 0})</div>
              </div>
            </div>

            <div className="admin-filters">
              <select value={fileFilter} onChange={(e) => setFileFilter(e.target.value)} className="admin-select">
                <option value="">All files</option>
                {uniqueFileKeys.map(fk => (
                  <option key={fk} value={fk}>{fk}</option>
                ))}
              </select>
              <input
                type="text"
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.target.value)}
                placeholder="Filter by email..."
                className="admin-search-input"
                onKeyDown={(e) => e.key === 'Enter' && handleFilterApply()}
              />
              <button className="admin-btn-filter" onClick={handleFilterApply}>Apply</button>
              {(fileFilter || emailSearch) && (
                <button className="admin-btn-clear" onClick={handleClearFilters}>Clear</button>
              )}
            </div>

            {logsError && <div className="admin-error">{logsError}</div>}

            {logsLoading ? (
              <div className="admin-loading">Loading logs...</div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>File</th>
                      <th>Date/Time</th>
                      <th>Tier</th>
                      <th>Last Login</th>
                      <th>User Agent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length === 0 ? (
                      <tr><td colSpan={6} className="admin-empty">No logs found</td></tr>
                    ) : (
                      logs.map((log, i) => {
                        const user = logUsers[log.email];
                        return (
                          <tr key={`${log.email}-${log.accessedAt}-${i}`}>
                            <td>{log.email}</td>
                            <td><span className="admin-file-badge">{log.fileKey}</span></td>
                            <td>{formatDate(log.accessedAt)}</td>
                            <td>{user?.effectiveTier || user?.subscriptionTier || '-'}</td>
                            <td>{user?.lastLogin ? formatDate(user.lastLogin) : '-'}</td>
                            <td title={log.userAgent}>{truncateUA(log.userAgent)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ===== REGIME TAB ===== */}
        {activeTab === 'regime' && <AdminRegime />}

        {activeTab === 'trade-signals-config' && <AdminTradeSignalsConfig />}

        {activeTab === 'page-view-stats' && <AdminPageViewStats />}

        {activeTab === 'calendar-notches' && <AdminCalendarNotches />}
        {activeTab === 'briefing-shadow' && <AdminBriefingShadow />}
      </div>
    </div>
  );
};
